import React from "react";
import { Link } from "react-router-dom";
import { IoMdClose } from "react-icons/io";
import { IoCalendarOutline, IoCheckmark, IoClipboardOutline } from "react-icons/io5";
import { BetDetails, ParlaySelection } from "../../../models/MyBetModel.tsx";
import {
    formatNumber,
    formatSpecialtyMarketLabel,
    getBetPrimaryName,
    getBetSelectionLabel,
    getBetSpecialtyMarketLabel,
} from "../../../helpers/functions.tsx";
import BetLogo from "./BetLogo.tsx";
import "../../../styles/StraightQuickSlip.css";

export type ConfirmLeg = BetDetails | ParlaySelection;

function formatConfirmLine(
    odd?: { points?: number | null; price?: number | null; selection_line?: string | null; market_id?: string | null } | null,
    marketName?: string | null,
) {
    if (!odd) return "";
    const isMoneyline =
        String(marketName ?? "").toLowerCase().includes("moneyline") ||
        String(odd.market_id ?? "").toLowerCase() === "moneyline";
    const price = odd.price != null ? formatNumber(odd.price) : "";
    if (isMoneyline || odd.points == null) return price;
    const line = String(odd.selection_line ?? "").toLowerCase();
    const points = formatNumber(odd.points, undefined, "points");
    if (line === "over" || line.startsWith("over")) return `O ${odd.points} (${price})`.trim();
    if (line === "under" || line.startsWith("under")) return `U ${odd.points} (${price})`.trim();
    return `${points} (${price})`;
}

function formatScheduled(gameDate?: { toDate?: () => Date; seconds?: number } | null) {
    if (!gameDate) return "";
    try {
        if (typeof gameDate.toDate === "function") {
            return gameDate.toDate().toLocaleString("en-US");
        }
        if (typeof gameDate.seconds === "number") {
            return new Date(gameDate.seconds * 1000).toLocaleString("en-US");
        }
    } catch {
        return "";
    }
    return "";
}

function amountValue(value?: number | null, decimals?: number) {
    const num = Number(value);
    if (!Number.isFinite(num)) return "";
    return decimals != null ? num.toFixed(decimals) : String(num);
}

function gameDateToDate(gameDate?: { toDate?: () => Date; seconds?: number } | null) {
    if (!gameDate) return null;
    try {
        const date = typeof gameDate.toDate === "function"
            ? gameDate.toDate()
            : typeof gameDate.seconds === "number"
                ? new Date(gameDate.seconds * 1000)
                : null;
        if (!date || Number.isNaN(date.getTime())) return null;
        return date;
    } catch {
        return null;
    }
}

function formatSportName(sport?: string | null) {
    const text = String(sport ?? "").trim();
    if (!text) return "";
    return text.replace(/\b([A-Za-z0-9]+)\b/g, (word) => {
        if (word.length <= 4 && word === word.toUpperCase()) return word;
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    });
}

function splitLegOdds(
    odd?: { points?: number | null; price?: number | null; selection_line?: string | null; market_id?: string | null } | null,
    marketName?: string | null,
) {
    if (!odd) return { line: "", price: "" };
    const isMoneyline =
        String(marketName ?? "").toLowerCase().includes("moneyline") ||
        String(odd.market_id ?? "").toLowerCase() === "moneyline";
    const price = odd.price != null ? formatNumber(odd.price) : "";
    if (isMoneyline || odd.points == null) return { line: price, price: "" };
    const selection = String(odd.selection_line ?? "").toLowerCase();
    if (selection === "over" || selection.startsWith("over")) return { line: `Over ${odd.points}`, price };
    if (selection === "under" || selection.startsWith("under")) return { line: `Under ${odd.points}`, price };
    return { line: formatNumber(odd.points, undefined, "points"), price };
}

function earliestScheduled(legs: ConfirmLeg[]) {
    let best: Date | null = null;
    for (const leg of legs) {
        const date = gameDateToDate(leg.gameDate);
        if (!date) continue;
        if (!best || date.getTime() < best.getTime()) best = date;
    }
    return best ? best.toLocaleString("en-US") : "";
}

function ConfirmTotals({ risk, win, decimals }: { risk: number; win: number; decimals?: number }) {
    return (
        <div className="sq-confirm__totals">
            <div className="sq-confirm__total sq-confirm__total--risk">
                <span className="sq-confirm__total-label">Risking</span>
                <div className="sq-confirm__total-value">
                    <span>$</span>
                    <input type="text" readOnly value={amountValue(risk, decimals)} tabIndex={-1} />
                </div>
            </div>
            <div className="sq-confirm__total sq-confirm__total--win">
                <span className="sq-confirm__total-label">To Win</span>
                <div className="sq-confirm__total-value">
                    <span>$</span>
                    <input type="text" readOnly value={amountValue(win, decimals)} tabIndex={-1} />
                </div>
            </div>
        </div>
    );
}

function ConfirmSummary({ risk, win }: { risk: number; win: number }) {
    return (
        <div className="sq-confirm__summary">
            <span className="sq-confirm__summary-label">Total</span>
            <div className="sq-confirm__summary-values">
                <span className="sq-confirm__summary-item">
                    <span>Risking</span>
                    <strong>${amountValue(risk)}</strong>
                </span>
                <span className="sq-confirm__summary-item sq-confirm__summary-item--win">
                    <span>To Win</span>
                    <strong>${amountValue(win)}</strong>
                </span>
            </div>
        </div>
    );
}

function legAmount(leg: ConfirmLeg, key: "bet" | "reward") {
    if (!(key in leg)) return 0;
    return Number(leg[key] ?? 0);
}

function legMarketLabel(leg: ConfirmLeg) {
    return getBetSpecialtyMarketLabel(leg)
        || formatSpecialtyMarketLabel(leg.marketName || leg.odd?.market);
}

function GroupedLeg({ leg, kind }: { leg: ConfirmLeg; kind: "parlay" | "teaser" }) {
    const pickName = getBetPrimaryName(leg);
    const marketLabel = legMarketLabel(leg);
    const subtitle = kind === "teaser" ? "Teaser Selection" : (marketLabel || "Parlay Selection");
    const { line, price } = splitLegOdds(leg.odd, leg.marketName);

    return (
        <div className="sq-confirm__group-leg">
            <BetLogo bet={leg} className="sport-logos-modal" />
            <div className="sq-slip__pick-meta">
                <span className="sq-slip__pick-name">{pickName}</span>
                {subtitle ? <span className="sq-slip__pick-market">{subtitle}</span> : null}
            </div>
            {(line || price) ? (
                <div className="sq-confirm__leg-odds">
                    {line ? <span className="sq-confirm__leg-line">{line}</span> : null}
                    {price ? <span className="sq-confirm__leg-price">{price}</span> : null}
                </div>
            ) : null}
        </div>
    );
}

function ConfirmGroup({
    legs,
    kind,
    oddsLabel,
    detailLabel,
}: {
    legs: ConfirmLeg[];
    kind: "parlay" | "teaser";
    oddsLabel?: string | null;
    detailLabel?: string | null;
}) {
    const noun = kind === "teaser" ? "Teaser" : "Parlay";
    const sportName = formatSportName(legs.find((leg) => leg.sport)?.sport);
    const title = [`${legs.length}-Team`, sportName, noun].filter(Boolean).join(" ");
    const selectionWord = legs.length === 1 ? "Selection" : "Selections";
    const subtitleLead = kind === "teaser" ? (detailLabel || noun) : noun;
    const subtitle = `${subtitleLead} • ${legs.length} ${selectionWord}`;
    const betType = kind === "teaser"
        ? (detailLabel || noun)
        : (oddsLabel ? `${noun} ${oddsLabel}` : noun);
    const scheduledLabel = earliestScheduled(legs);
    const pickWord = legs.length === 1 ? "Pick" : "Picks";

    return (
        <div className="sq-confirm__group">
            <div className="sq-confirm__group-head">
                <div className="sq-confirm__group-copy">
                    <span className="sq-confirm__group-title">{title}</span>
                    <span className="sq-confirm__group-sub">{subtitle}</span>
                </div>
                <span className="sq-confirm__group-picks">{legs.length} {pickWord}</span>
            </div>
            <div className="sq-confirm__group-legs">
                {legs.map((leg, index) => (
                    <GroupedLeg key={`${leg.ticket ?? "leg"}-${index}`} leg={leg} kind={kind} />
                ))}
            </div>
            <div className="sq-confirm__meta">
                {scheduledLabel ? (
                    <div className="sq-confirm__meta-row">
                        <IoCalendarOutline className="sq-confirm__meta-icon" aria-hidden="true" />
                        <span>
                            <span className="sq-confirm__meta-label">Scheduled</span>
                            <span className="sq-confirm__meta-value">{scheduledLabel}</span>
                        </span>
                    </div>
                ) : null}
                <div className="sq-confirm__meta-row">
                    <IoClipboardOutline className="sq-confirm__meta-icon" aria-hidden="true" />
                    <span>
                        <span className="sq-confirm__meta-label">Bet Type</span>
                        <span className="sq-confirm__meta-value">{betType}</span>
                    </span>
                </div>
            </div>
        </div>
    );
}

function ConfirmCard({ leg, showAmounts }: { leg: ConfirmLeg; showAmounts?: boolean }) {
    const pickName = getBetPrimaryName(leg);
    const specialtyMarketLabel = getBetSpecialtyMarketLabel(leg);
    const marketLabel =
        specialtyMarketLabel
        || formatSpecialtyMarketLabel(leg.marketName || leg.odd?.market);
    const scheduledLabel = formatScheduled(leg.gameDate);
    const selectionLabel = getBetSelectionLabel(leg);
    const oddsLabel = formatConfirmLine(leg.odd, leg.marketName);
    const gameType = "gameType" in leg ? leg.gameType : "";
    const isLive = String(gameType ?? "").toLowerCase().includes("live");

    return (
        <div className="sq-confirm__card">
            <div className="sq-slip__pick">
                <BetLogo bet={leg} className="sport-logos-modal" />
                <div className="sq-slip__pick-meta">
                    <span className="sq-slip__pick-name">{pickName}</span>
                    {marketLabel ? <span className="sq-slip__pick-market">{marketLabel}</span> : null}
                    {isLive && (
                        <span className="fw-bold inter live-bet-label">
                            Live
                            <span className="live-text-dot" aria-hidden="true" />
                        </span>
                    )}
                </div>
                {oddsLabel ? (
                    <div className="sq-slip__odds">
                        <span className="sq-confirm__odds">{oddsLabel}</span>
                    </div>
                ) : null}
            </div>

            {(scheduledLabel || selectionLabel) ? (
                <div className="sq-confirm__meta">
                    {scheduledLabel ? (
                        <div className="sq-confirm__meta-row">
                            <IoCalendarOutline className="sq-confirm__meta-icon" aria-hidden="true" />
                            <span>
                                <span className="sq-confirm__meta-label">Scheduled</span>
                                <span className="sq-confirm__meta-value">{scheduledLabel}</span>
                            </span>
                        </div>
                    ) : null}
                    {selectionLabel ? (
                        <div className="sq-confirm__meta-row">
                            <IoClipboardOutline className="sq-confirm__meta-icon" aria-hidden="true" />
                            <span>
                                <span className="sq-confirm__meta-label">Selection</span>
                                <span className="sq-confirm__meta-value">{selectionLabel}</span>
                            </span>
                        </div>
                    ) : null}
                </div>
            ) : null}

            {showAmounts ? (
                <ConfirmTotals risk={legAmount(leg, "bet")} win={legAmount(leg, "reward")} />
            ) : null}
        </div>
    );
}

const WagerConfirmedSlip = ({
    legs,
    risk,
    win,
    ticketNumber,
    onClose,
    showLegAmounts = false,
    kind = "straight",
    oddsLabel,
    detailLabel,
}: {
    legs: ConfirmLeg[];
    risk: number;
    win: number;
    ticketNumber: number | string | null;
    onClose: () => void;
    showLegAmounts?: boolean;
    kind?: "straight" | "parlay" | "teaser";
    oddsLabel?: string | null;
    detailLabel?: string | null;
}) => {
    const count = Math.max(legs.length, 1);
    const perLeg = showLegAmounts && legs.length > 1;
    const grouped = kind === "parlay" || kind === "teaser";
    const compact = legs.length > 1;

    return (
        <div className={`sq-slip sq-confirm${compact ? " sq-confirm--compact" : ""}`}>
            <div className="sq-slip__header">
                <div className="sq-slip__header-left">
                    <span className={`sq-slip__count${grouped ? " sq-slip__count--check" : ""}`}>
                        {grouped ? <IoCheckmark /> : count}
                    </span>
                    <h1 className="sq-slip__title">Wager Confirmed</h1>
                </div>
                <button type="button" className="sq-slip__close" onClick={onClose} aria-label="Close">
                    <IoMdClose size={25} />
                </button>
            </div>

            <div className="sq-slip__body">
                {kind === "parlay" || kind === "teaser" ? (
                    <ConfirmGroup
                        legs={legs}
                        kind={kind}
                        oddsLabel={oddsLabel}
                        detailLabel={detailLabel}
                    />
                ) : (
                    <div className="sq-confirm__cards">
                        {legs.map((leg, index) => (
                            <ConfirmCard
                                key={`${leg.ticket ?? "leg"}-${index}`}
                                leg={leg}
                                showAmounts={perLeg}
                            />
                        ))}
                    </div>
                )}

                {perLeg ? (
                    <ConfirmSummary risk={risk} win={win} />
                ) : (
                    <ConfirmTotals risk={risk} win={win} decimals={grouped ? 2 : undefined} />
                )}

                <div className="d-flex justify-content-center">
                    <span className="ticket">Ticket: {ticketNumber}</span>
                </div>
                <div className="sq-slip__actions">
                    <Link to="/bets" className="sq-slip__btn sq-slip__btn--back" onClick={onClose}>
                        Pending
                    </Link>
                    <button type="button" className="sq-slip__btn sq-slip__btn--confirm" onClick={onClose}>
                        Continue
                    </button>
                </div>
            </div>
        </div>
    );
};

export default WagerConfirmedSlip;
